import type { NodeType } from '../../types/flow-types';

export interface FlowTemplateNode {
	key: string;
	type: NodeType;
	paletteKey?: string;
	/** Intent-specific defaults layered over the card definition defaults. */
	data?: Record<string, unknown>;
	offset: { x: number; y: number };
}

export interface FlowTemplateEdge {
	from: string;
	to: string;
	branch?: string;
}

export interface FlowTemplate {
	id: string;
	name: string;
	description: string;
	domain: string;
	outcome: string;
	icon: string;
	color: string;
	nodes: FlowTemplateNode[];
	edges: FlowTemplateEdge[];
}

export const FLOW_TEMPLATES: readonly FlowTemplate[] = [
	{
		id: 'motion-light',
		name: 'Motion-activated light',
		description: 'When presence is detected, turn on a light; otherwise turn it off.',
		domain: 'Lighting',
		outcome: 'A light follows occupancy',
		icon: 'fa-person-walking',
		color: '#3b82f6',
		nodes: [
			{ key: 'motion', type: 'condition', offset: { x: 0, y: 0 } },
			{ key: 'on', type: 'action', offset: { x: 300, y: -80 } },
			{ key: 'off', type: 'action', offset: { x: 300, y: 90 } },
		],
		edges: [
			{ from: 'motion', to: 'on', branch: 'true' },
			{ from: 'motion', to: 'off', branch: 'false' },
		],
	},
	{
		id: 'threshold-alert',
		name: 'Threshold alert',
		description: 'Watch a sensor value and send a notification when it crosses a threshold.',
		domain: 'Safety',
		outcome: 'A threshold creates an alert',
		icon: 'fa-temperature-high',
		color: '#f97316',
		nodes: [
			{
				key: 'sensor',
				type: 'condition',
				paletteKey: 'deviceCheck',
				offset: { x: 0, y: 0 },
			},
			{ key: 'notify', type: 'notificationAction', offset: { x: 300, y: 0 } },
		],
		edges: [{ from: 'sensor', to: 'notify', branch: 'true' }],
	},
	{
		id: 'delayed-shutoff',
		name: 'Delayed shutoff',
		description: 'Turn a device on, wait for a chosen duration, then turn it off.',
		domain: 'Core',
		outcome: 'A device turns off automatically',
		icon: 'fa-hourglass-half',
		color: '#8b5cf6',
		nodes: [
			{ key: 'start', type: 'manualCondition', offset: { x: 0, y: 0 } },
			{ key: 'on', type: 'action', offset: { x: 260, y: 0 } },
			{ key: 'wait', type: 'timer', offset: { x: 520, y: 0 } },
			{ key: 'off', type: 'action', offset: { x: 780, y: 0 } },
		],
		edges: [
			{ from: 'start', to: 'on', branch: 'true' },
			{ from: 'on', to: 'wait' },
			{ from: 'wait', to: 'off' },
		],
	},
	{
		id: 'multi-case',
		name: 'Multi-case response',
		description: 'Choose among several execution paths from one device or variable value.',
		domain: 'Core',
		outcome: 'One value selects one path',
		icon: 'fa-shuffle',
		color: '#e879f9',
		nodes: [
			{ key: 'switch', type: 'switchNode', offset: { x: 0, y: 0 } },
			{ key: 'case1', type: 'action', offset: { x: 330, y: -100 } },
			{ key: 'case2', type: 'action', offset: { x: 330, y: 40 } },
			{
				key: 'fallback',
				type: 'notificationAction',
				offset: { x: 330, y: 180 },
			},
		],
		edges: [
			{ from: 'switch', to: 'case1', branch: 'case-0' },
			{ from: 'switch', to: 'case2', branch: 'case-1' },
			{ from: 'switch', to: 'fallback', branch: 'default' },
		],
	},
	{
		id: 'presence-object-count',
		name: 'Presence object count',
		description: 'Compare how many people or objects are currently visible in a Presence zone.',
		domain: 'Presence',
		outcome: 'A zone count selects the true or false path',
		icon: 'fa-people-group',
		color: '#06b6d4',
		nodes: [
			{
				key: 'count',
				type: 'presenceCondition',
				data: { presenceEvent: 'object_count', objectOperator: 'gt', objectCount: 1 },
				offset: { x: 0, y: 0 },
			},
			{ key: 'matched', type: 'notificationAction', offset: { x: 330, y: -70 } },
			{
				key: 'not-matched',
				type: 'notificationAction',
				paletteKey: 'eventLogNotification',
				offset: { x: 330, y: 90 },
			},
		],
		edges: [
			{ from: 'count', to: 'matched', branch: 'true' },
			{ from: 'count', to: 'not-matched', branch: 'false' },
		],
	},
	{
		id: 'occupancy-hold',
		name: 'Occupancy hold',
		description: 'Wait after occupancy is detected before applying the next action.',
		domain: 'Presence',
		outcome: 'Short occupancy changes do not cause immediate actions',
		icon: 'fa-person-circle-check',
		color: '#14b8a6',
		nodes: [
			{
				key: 'presence',
				type: 'presenceCondition',
				data: { presenceEvent: 'presence' },
				offset: { x: 0, y: 0 },
			},
			{
				key: 'hold',
				type: 'timer',
				data: { delay: { value: 5, units: 'min' } },
				offset: { x: 300, y: 0 },
			},
			{ key: 'action', type: 'action', offset: { x: 600, y: 0 } },
		],
		edges: [
			{ from: 'presence', to: 'hold', branch: 'true' },
			{ from: 'hold', to: 'action' },
		],
	},
	{
		id: 'adaptive-lighting',
		name: 'Adaptive evening lighting',
		description: 'Start a lighting action at sunset, with an optional home-location offset.',
		domain: 'Lighting',
		outcome: 'Lighting follows the local sunset',
		icon: 'fa-lightbulb',
		color: '#f59e0b',
		nodes: [
			{
				key: 'sunset',
				type: 'sunriseSunsetCondition',
				data: { sunType: 'sunset', beforeAfter: 'after', minutes: 0 },
				offset: { x: 0, y: 0 },
			},
			{ key: 'lights', type: 'action', offset: { x: 320, y: 0 } },
		],
		edges: [{ from: 'sunset', to: 'lights', branch: 'true' }],
	},
	{
		id: 'climate-comfort-band',
		name: 'Climate comfort band',
		description: 'Use hysteresis to react above and below a comfortable sensor range.',
		domain: 'Climate',
		outcome: 'Heating or ventilation avoids rapid switching',
		icon: 'fa-temperature-half',
		color: '#14b8a6',
		nodes: [
			{
				key: 'band',
				type: 'hysteresisCondition',
				data: { lowerThreshold: 20, upperThreshold: 22 },
				offset: { x: 0, y: 0 },
			},
			{ key: 'above', type: 'action', offset: { x: 330, y: -80 } },
			{ key: 'below', type: 'action', offset: { x: 330, y: 100 } },
		],
		edges: [
			{ from: 'band', to: 'above', branch: 'true' },
			{ from: 'band', to: 'below', branch: 'false' },
		],
	},
	{
		id: 'energy-peak-guard',
		name: 'Energy peak guard',
		description: 'Watch live power and switch or notify when consumption exceeds a limit.',
		domain: 'Energy',
		outcome: 'Peak consumption triggers load reduction',
		icon: 'fa-bolt-lightning',
		color: '#eab308',
		nodes: [
			{
				key: 'power',
				type: 'condition',
				paletteKey: 'deviceCheck',
				data: { executionMode: 'once' },
				offset: { x: 0, y: 0 },
			},
			{ key: 'reduce', type: 'action', offset: { x: 320, y: -60 } },
			{
				key: 'log',
				type: 'notificationAction',
				paletteKey: 'eventLogNotification',
				offset: { x: 610, y: -60 },
			},
		],
		edges: [
			{ from: 'power', to: 'reduce', branch: 'true' },
			{ from: 'reduce', to: 'log' },
		],
	},
	{
		id: 'safe-cover-close',
		name: 'Safe cover close',
		description: 'Check a door, window, or obstruction sensor before closing a cover.',
		domain: 'Covers & access',
		outcome: 'The cover closes only when the path is safe',
		icon: 'fa-door-closed',
		color: '#6366f1',
		nodes: [
			{
				key: 'safe',
				type: 'condition',
				paletteKey: 'deviceCheck',
				offset: { x: 0, y: 0 },
			},
			{ key: 'close', type: 'action', offset: { x: 330, y: -70 } },
			{ key: 'blocked', type: 'notificationAction', offset: { x: 330, y: 100 } },
		],
		edges: [
			{ from: 'safe', to: 'close', branch: 'true' },
			{ from: 'safe', to: 'blocked', branch: 'false' },
		],
	},
	{
		id: 'leak-response',
		name: 'Leak response',
		description: 'When a leak sensor activates, shut off water and send an alert.',
		domain: 'Safety & security',
		outcome: 'Water is shut off and the household is notified',
		icon: 'fa-droplet-slash',
		color: '#ef4444',
		nodes: [
			{ key: 'leak', type: 'condition', offset: { x: 0, y: 0 } },
			{ key: 'shutoff', type: 'action', offset: { x: 300, y: 0 } },
			{ key: 'alert', type: 'notificationAction', offset: { x: 600, y: 0 } },
		],
		edges: [
			{ from: 'leak', to: 'shutoff', branch: 'true' },
			{ from: 'shutoff', to: 'alert' },
		],
	},
	{
		id: 'irrigation-window',
		name: 'Weather-aware irrigation',
		description: 'Run irrigation only inside a weekly window and when weather criteria allow it.',
		domain: 'Water & environment',
		outcome: 'Irrigation respects both schedule and weather',
		icon: 'fa-seedling',
		color: '#22c55e',
		nodes: [
			{ key: 'schedule', type: 'scheduleCondition', offset: { x: 0, y: 0 } },
			{ key: 'weather', type: 'weatherCondition', offset: { x: 300, y: 0 } },
			{ key: 'water', type: 'action', offset: { x: 600, y: 0 } },
		],
		edges: [
			{ from: 'schedule', to: 'weather', branch: 'true' },
			{ from: 'weather', to: 'water', branch: 'true' },
		],
	},
];

export function getFlowTemplate(id: string): FlowTemplate | undefined {
	return FLOW_TEMPLATES.find((template) => template.id === id);
}
